import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Head } from 'vite-react-ssg'
import { getCanonicalUrl } from '../utils/canonical'
import {
  Zap,
  Shield,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Lightbulb,
  Scale,
  Palette
} from 'lucide-react'
import { ContrastCheckerWidget } from '../components/ContrastChecker'

export default function ContrastCheckerPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  const contrastRatios = [
    { level: 'WCAG AA', normalText: '4.5:1', largeText: '3:1' },
    { level: 'WCAG AAA', normalText: '7:1', largeText: '4.5:1' }
  ]

  const fixStrategies = [
    {
      title: 'Adjust Lightness',
      description: 'This is the most effective fix. Keep the hue and saturation the same, but make the lighter color lighter or the darker color darker.'
    },
    {
      title: 'Use an Outline or Shadow',
      description: 'For text on a busy image or gradient, adding a subtle but high-contrast text-shadow can make it readable.'
    },
    {
      title: 'Add a Background Layer',
      description: 'For buttons or callouts with low-contrast brand colors, place them inside a container with a compliant background.'
    },
    {
      title: 'Increase Font Weight',
      description: 'Bumping text up from normal to bold can significantly improve legibility, even if it doesn\'t change the contrast ratio itself.'
    }
  ]

  const edgeCases = [
    {
      case: 'Gradients',
      tip: 'When testing text against a gradient, you must test the part of the gradient with the lowest contrast.'
    },
    {
      case: 'Text on Images',
      tip: 'Ensure the text has sufficient contrast against every part of the image it overlaps. A semi-transparent overlay behind the text is a common and effective technique.'
    },
    {
      case: 'Component States',
      tip: 'Remember to check the contrast for all interactive states: hover, focus, and active. The focus indicator must have a 3:1 contrast against its background.'
    }
  ]

  const legalInfo = [
    {
      region: 'USA: DOJ Title II and Section 508',
      description: 'In the US, Section 508 requires federal agencies to follow WCAG. For state and local governments, a 2024 Department of Justice rule formally adopted WCAG 2.1 AA under the Americans with Disabilities Act (ADA).'
    },
    {
      region: 'EU, UK, Canada, and Australia',
      description: 'Across the European Union, the UK, Canada, and Australia, regulations for public sector bodies and increasingly for the private sector are all harmonized with the WCAG 2.1 AA standard. Meeting this level is the safest path to global compliance.'
    }
  ]

  const faqs = [
    {
      question: 'My brand colors fail the contrast test. What should I do?',
      answer: 'First, see if a slightly darker or lighter shade of your brand color can pass. If not, you may need to create a specific, accessible color palette for digital use. For elements like logos, which are exempt, you don\'t need to change them. But for all functional text and UI, you must use a compliant alternative.'
    },
    {
      question: 'Can I use a color that is "just barely" 4.5:1?',
      answer: 'Yes. If the checker shows a ratio of 4.5:1 or higher, you pass. A ratio of 4.49:1 is a fail. There is no gray area. However, for better readability, aiming for a higher ratio is always a good user experience practice.'
    },
    {
      question: 'How do I test the contrast of icons?',
      answer: 'Treat meaningful icons just like you would UI components. The icon\'s color must have a 3:1 contrast ratio against its background. If the icon is purely decorative and provides no information, it doesn\'t need to meet contrast requirements.'
    },
    {
      question: 'What about text in PDFs, mobile apps, or videos?',
      answer: 'The WCAG principles apply to all digital content, not just websites. Text in PDFs, captions in videos, and UI elements in mobile applications must all meet the same contrast requirements to be considered accessible.'
    },
    {
      question: 'What is considered "large text" again?',
      answer: 'WCAG defines large text as anything that is 18 points (pt) or larger, or 14 points or larger if it is also bold. This is roughly equivalent to 24 pixels (px) for normal weight or 18.7px for bold weight, though the exact pixel size can vary by font.'
    }
  ]

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index)
  }

  return (
    <>
      <Head>
        <title>Color Contrast Checker: Your Guide to WCAG 2.2 & Accessibility</title>
        <meta name="description" content="Instantly test your color combinations with the free AChecker Contrast Checker. Master WCAG ratios, non-text contrast, and global compliance laws." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={getCanonicalUrl('/contrast-checker')} />
      </Head>

      <div className="space-y-12">
        {/* Hero Section */}
        <section className="text-center py-12 bg-gradient-to-br from-purple-50 to-violet-100 rounded-2xl">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Contrast Checker: Instantly Test and Fix Your WCAG Color Ratios
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-4xl mx-auto px-4">
            Good design is accessible design. The foundation of digital accessibility starts with a simple but critical element: <strong>color contrast</strong>. Get it right, and you create clarity for everyone. Get it wrong, and you exclude millions of users and risk legal non-compliance.
          </p>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto px-4">
            Use the free AChecker Contrast Checker below to get an instant pass/fail result, then read on to understand the rules, fix common issues, and ensure your digital products are compliant and usable for all.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#contrast-checker-section"
              className="inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
              onClick={(e) => {
                e.preventDefault()
                document.getElementById('contrast-checker-section')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              <Zap className="w-5 h-5 mr-2" />
              Check Contrast Now
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-gray-700 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <Shield className="w-5 h-5 mr-2" />
              Full Accessibility Checker
            </Link>
          </div>
        </section>

        {/* Contrast Checker Widget */}
        <ContrastCheckerWidget />

        {/* What Is a Color Contrast Checker? */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">What Is a Color Contrast Checker?</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-6">
              A color contrast checker is a tool that measures the difference in perceived luminance (or brightness) between two colors: a <strong>foreground color</strong> (like text or an icon) and its <strong>background</strong>. It calculates this difference as a ratio, such as <code className="bg-gray-100 px-2 py-0.5 rounded">4.5:1</code>. This ratio is then compared against the standards set by the <strong>Web Content Accessibility Guidelines (WCAG)</strong> to determine if there is enough contrast for people with visual impairments to read and interact with your content.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <div className="text-2xl font-bold text-purple-700">4.5:1</div>
                <div className="text-sm text-purple-600">Normal text (WCAG AA)</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <div className="text-2xl font-bold text-purple-700">3:1</div>
                <div className="text-sm text-purple-600">Large text (WCAG AA)</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <div className="text-2xl font-bold text-purple-700">7:1</div>
                <div className="text-sm text-purple-600">Normal text (WCAG AAA)</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <div className="text-2xl font-bold text-purple-700">3:1</div>
                <div className="text-sm text-purple-600">UI components (WCAG AA)</div>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-4">
              <strong>Important:</strong> WCAG does not permit rounding. A ratio of <code className="bg-gray-100 px-2 py-0.5 rounded">4.499:1</code> is a failure.
            </p>
          </div>
        </section>

        {/* How to Use Guide */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How to Use the AChecker Contrast Checker: A Step-by-Step Guide</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-6">
              Our tool gives you actionable results in under a minute. Here's how to use it for text, user interface (UI) components, and graphics.
            </p>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Input Your Foreground and Background Colors</h3>
                  <p className="text-gray-600">Use the color pickers or enter the HEX or RGB values for the two colors you want to test. For example, test your primary button text color against the button's background color.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Select Text Size and Font Weight</h3>
                  <p className="text-gray-600">WCAG has different requirements for normal and large text. Select the correct option to get an accurate result.</p>
                  <p className="text-sm text-gray-500 mt-2">
                    <strong>What is "Large Text"?</strong> Text that is at least <strong>18pt</strong> (approximately 24px) or <strong>14pt</strong> (approximately 18.7px) and <strong>bold</strong>.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Interpret the Pass/Fail Results</h3>
                  <p className="text-gray-600">The tool will instantly show you the contrast ratio and whether it passes or fails WCAG Level AA and the stricter Level AAA. For most legal and commercial purposes, <strong>Level AA is the target standard.</strong></p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Check Non-Text Elements (Icons, Borders, Charts)</h3>
                  <p className="text-gray-600">Contrast isn't just for text. Use the "UI Components" mode to check important non-text elements against the <strong>3:1 ratio</strong>. This includes borders of input fields, checkmarks, slider thumbs, chart segments, and focus indicators.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WCAG Contrast Ratios Table */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding WCAG Contrast Ratios in Detail</h2>
          <p className="text-gray-700 mb-6">
            The numbers aren't arbitrary. They are based on extensive research into human vision and readability detailed in the{' '}
            <a href="https://www.w3.org/WAI/WCAG22/Understanding/contrast-minimum.html" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">
              Web Content Accessibility Guidelines
            </a>.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-sm border border-gray-200">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Conformance Level</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Normal Text (&lt;18pt or &lt;14pt bold)</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Large Text (18pt+ or 14pt+ bold)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {contrastRatios.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{row.level}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 font-mono">{row.normalText}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 font-mono">{row.largeText}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Palette className="w-5 h-5 mr-2 text-purple-600" />
              Beyond Text: The 3:1 Rule for Non-Text Contrast
            </h3>
            <p className="text-gray-700">
              Introduced in WCAG 2.1,{' '}
              <a href="https://www.w3.org/WAI/WCAG22/Understanding/non-text-contrast.html" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">
                Success Criterion 1.4.11 Non-text Contrast
              </a>{' '}
              was a game-changer. It mandates a <strong>3:1 contrast ratio</strong> for user interface components (like input borders and checkboxes) and essential parts of graphics (like bars in a bar chart).
            </p>
          </div>
        </section>

        {/* Fixing Contrast Failures */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">A Practical Guide to Fixing Contrast Failures</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-6">
              So you've found a color combination that fails. Don't panic. You can often fix it without a complete brand redesign.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2 text-yellow-500" />
              How to Adjust Colors Without Breaking Your Brand
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {fixStrategies.map((strategy, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{strategy.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{strategy.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">Mastering Edge Cases: Gradients, Overlays, and States</h3>
            <div className="space-y-4">
              {edgeCases.map((item, index) => (
                <div key={index} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <span className="font-semibold text-yellow-800">{item.case}:</span>
                  <span className="text-yellow-700 ml-2">{item.tip}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Legal Compliance */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Contrast Matters: Global Accessibility Laws at a Glance</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-gray-700 mb-6">
              Digital accessibility is not just a best practice; it's a legal requirement in most parts of the world. Failing to meet WCAG contrast standards can expose organizations to lawsuits and fines. While laws vary, they almost universally point to <strong>WCAG 2.1 Level AA</strong> as the benchmark.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {legalInfo.map((item, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Scale className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-gray-900">{item.region}</h4>
                      <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* APCA Section */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Future of Contrast: A Quick Look at APCA</h2>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-gray-700">
              You may hear about the <strong>Advanced Perceptual Contrast Algorithm (APCA)</strong>. It's a new method for calculating contrast that is a candidate for the future WCAG 3.0. However, for now, <strong>APCA is not the legal standard</strong>. All current laws and regulations are based on the WCAG 2.x contrast ratio algorithm. This tool uses the official WCAG 2.x formula.
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions about Contrast Checking</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                  aria-expanded={openFaq === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3 className="text-lg font-medium text-gray-900">{faq.question}</h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" aria-hidden="true" />
                  )}
                </button>
                {openFaq === index && (
                  <div id={`faq-answer-${index}`} className="px-6 pb-4">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center py-12 bg-gradient-to-r from-purple-600 to-violet-600 rounded-2xl text-white">
          <h2 className="text-3xl font-bold mb-4">
            Your Next Step: Go Beyond Contrast with a Full AChecker Audit
          </h2>
          <p className="text-xl mb-4 opacity-90 max-w-3xl mx-auto">
            Perfecting your color contrast is a huge step toward an accessible website. But it's just one piece of the puzzle.
          </p>
          <p className="text-lg mb-8 opacity-80 max-w-3xl mx-auto">
            A truly accessible experience also requires keyboard navigability, proper alt text for images, semantic HTML, and much more. Use our full Website Accessibility Checker to run a comprehensive audit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              <Zap className="w-5 h-5 mr-2" />
              Run Full Accessibility Audit
            </Link>
            <Link
              to="/wcag-validator"
              className="inline-flex items-center justify-center px-8 py-3 bg-purple-700 text-white rounded-lg font-semibold hover:bg-purple-800 transition-colors border border-purple-500"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              WCAG Validator
            </Link>
          </div>
        </section>
      </div>
    </>
  )
}
